import React, { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { heroSlides } from "../mock";
import Editable from "./Editable";

export default function HeroCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 30 });
  const [selected, setSelected] = useState(0);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    // Autoplay — slower speed for more reading time
    const id = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 10000);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section className="relative overflow-hidden" style={{ minHeight: "88vh" }}>
      <div ref={emblaRef} className="absolute inset-0 overflow-hidden">
        <div className="flex h-full">
          {heroSlides.map((slide, i) => (
            <div
              key={i}
              className="relative flex-[0_0_100%] h-full min-h-[88vh] bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              {/* Subtle bottom-vignette only — keeps background image fully visible */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent pointer-events-none" />
              <div className="relative max-w-7xl mx-auto px-6 pt-28 pb-24 md:pt-36 md:pb-32 text-white h-full flex items-center">
                <div className="max-w-3xl rounded-2xl p-8 md:p-10 backdrop-blur-md border border-white/15 shadow-2xl" style={{ background: "linear-gradient(135deg, rgba(14,42,28,0.82) 0%, rgba(26,77,46,0.72) 60%, rgba(45,106,79,0.62) 100%)" }}>
                  <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/90 bg-white/10 backdrop-blur-md border border-white/25 rounded-full px-4 py-2 mb-6">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--brand-300)" }} />
                    <Editable field={`hero.slide.${i}.kicker`} as="span">{slide.kicker}</Editable>
                  </p>
                  <h1 className="font-display font-bold text-4xl md:text-6xl leading-[1.05] tracking-tight">
                    <Editable field={`hero.slide.${i}.title`} as="span">{slide.title}</Editable>
                  </h1>
                  <p className="mt-5 text-lg md:text-xl text-white/90 max-w-2xl">
                    <Editable field={`hero.slide.${i}.subtitle`} as="span" multiline>{slide.subtitle}</Editable>
                  </p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <Link to="/about-us">
                      <Button className="text-white rounded-md h-12 px-7 font-medium hover:opacity-95" style={{ backgroundColor: "var(--brand-500)" }}>
                        Learn More <ArrowRight size={16} className="ml-2" />
                      </Button>
                    </Link>
                    <Link to="/contact-us">
                      <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-md h-12 px-7 font-medium backdrop-blur-md">
                        Get a Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={scrollPrev}
        aria-label="Previous slide"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center bg-white/10 hover:bg-white/25 backdrop-blur-md border border-white/25 text-white transition z-10"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={scrollNext}
        aria-label="Next slide"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center bg-white/10 hover:bg-white/25 backdrop-blur-md border border-white/25 text-white transition z-10"
      >
        <ChevronRight size={20} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2.5 z-10">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${
              i === selected ? "w-10 bg-white" : "w-4 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
